import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  EditableCollectionBinding,
  EditableSlotGroup,
  EditableToken,
  SectionContextProvider,
  useBlockSlot,
  useCollectionBinding,
  useContentToken,
  useProperty,
  usePropertyAddressUrl,
  useSlotGroup,
} from '@vibesites/runtime/react';
import { Mail, MapPin, Phone } from 'lucide-react';
import { formatPhone, groupOfficeHours, hasValue, telHref, type OfficeHour } from '../utils/content';

const addressDef: SlotGroupDefinition = {
  label: 'Office Address',
  fields: {
    address: { label: 'Address', type: 'text', defaultField: 'property_full_address' },
    url: { label: 'Directions URL (defaults to Google Maps)', type: 'url', defaultValue: '' },
    _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
  },
};

export function ContactInfo() {
  const property = useProperty() as unknown as Record<string, unknown>;
  const directionsUrl = usePropertyAddressUrl();
  const officeHeading = useContentToken('contactinfo.office_heading');
  const hoursHeading = useContentToken('contactinfo.hours_heading');
  const phone = useContentToken('contactinfo.phone');
  const email = useContentToken('contactinfo.email');
  const address = useSlotGroup('contactinfo.address', addressDef);
  const hours = groupOfficeHours(useCollectionBinding<OfficeHour>('contactinfo.hours', { collection: 'office_hours' }) || []);
  const form = useBlockSlot('contactinfo.form', { defaultValue: 'multihub-lead-form' });

  const phoneValue = hasValue(phone) ? phone : String(property?.property_phone ?? '');
  const headingClass = 'js-heading text-[30px] text-secondary sm:text-[36px]';

  return (
    <section className="js-contact bg-background pb-16 pt-6 md:pb-24">
      <div className="mx-auto grid max-w-[1180px] gap-12 px-6 lg:grid-cols-[minmax(0,5fr)_minmax(0,7fr)] lg:gap-16 lg:px-10">
        <div className="js-stagger flex flex-col gap-10 pt-2">
          <div>
            <EditableToken path="contactinfo.office_heading" as="h2" className={headingClass}>
              {officeHeading}
            </EditableToken>
            <ul className="mt-5 flex flex-col gap-4 font-body text-[18px] text-foreground">
              <li>
                <EditableSlotGroup groupPath="contactinfo.address" definition={addressDef}>
                  <a
                    href={address.url || directionsUrl || '#'}
                    target={address._target || '_blank'}
                    rel="noopener noreferrer"
                    className="group inline-flex items-start gap-3 text-foreground no-underline transition-colors duration-300 hover:text-primary"
                  >
                    <MapPin className="mt-1 h-5 w-5 shrink-0 text-primary transition-transform duration-300 ease-out group-hover:-translate-y-0.5" aria-hidden />
                    <span>{address.address || String(property?.property_full_address ?? '')}</span>
                  </a>
                </EditableSlotGroup>
              </li>
              {hasValue(phoneValue) && (
                <li>
                  <a href={telHref(phoneValue)} className="group inline-flex items-center gap-3 text-foreground no-underline transition-colors duration-300 hover:text-primary">
                    <Phone className="h-5 w-5 shrink-0 text-primary transition-transform duration-300 ease-out group-hover:rotate-12" aria-hidden />
                    <EditableToken path="contactinfo.phone" as="span">
                      {formatPhone(phoneValue)}
                    </EditableToken>
                  </a>
                </li>
              )}
              {hasValue(email) && (
                <li>
                  <a href={`mailto:${email}`} className="group inline-flex items-center gap-3 break-all text-foreground no-underline transition-colors duration-300 hover:text-primary">
                    <Mail className="h-5 w-5 shrink-0 text-primary transition-transform duration-300 ease-out group-hover:-translate-y-0.5" aria-hidden />
                    <EditableToken path="contactinfo.email" as="span">
                      {email}
                    </EditableToken>
                  </a>
                </li>
              )}
            </ul>
          </div>

          <div>
            <EditableToken path="contactinfo.hours_heading" as="h2" className={headingClass}>
              {hoursHeading}
            </EditableToken>
            <EditableCollectionBinding bindingPath="contactinfo.hours" label="Office Hours">
              {hours.length > 0 ? (
                <dl className="mt-5 grid max-w-[360px] grid-cols-[auto_1fr] gap-x-8 gap-y-3 font-body text-[18px] text-foreground">
                  {hours.map((h) => (
                    <div key={h.label} className="contents">
                      <dt className="whitespace-nowrap">{h.label}</dt>
                      <dd className="whitespace-nowrap">{h.hours}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="js-copy mt-5 text-[color:var(--vibe-color-text-muted)]">Call or email us to set up a time that works for you.</p>
              )}
            </EditableCollectionBinding>
          </div>
        </div>

        <div className="js-reveal-right js-d1">
          <EditableToken path="contactinfo.form" type="block" label="Lead Form" as="div">
            {form ? (
              <SectionContextProvider sectionId={form.sectionId}>
                <form.BlockComponent sectionId={form.sectionId} />
              </SectionContextProvider>
            ) : (
              <div className="rounded-lg bg-muted p-8 text-center text-[color:var(--vibe-color-text-muted)]">No form selected</div>
            )}
          </EditableToken>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ContactInfo',
  displayName: 'Contact Details & Form',
  tokens: {
    'contactinfo.office_heading': { label: 'Office Heading', type: 'text', defaultValue: 'Leasing Office' },
    'contactinfo.hours_heading': { label: 'Hours Heading', type: 'text', defaultValue: 'Hours' },
    'contactinfo.phone': {
      label: 'Phone',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
    'contactinfo.email': { label: 'Email', type: 'email', defaultField: 'property_email' },
    'contactinfo.form': {
      label: 'Form',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
  },
  slotGroups: {
    'contactinfo.address': addressDef,
  },
  collectionBindings: {
    'contactinfo.hours': { label: 'Office Hours', collection: 'office_hours' },
  },
};

(ContactInfo as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ContactInfo;
